import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemText,
  Modal,
  TextField,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useRefMounted } from '@/common/hooks/useRefMounted';
import { useFormik } from 'formik';
import { createWorkspace } from '@/workspaces/services/createWorkspace';
import { useUserStore } from '@/user/stores/userStore';
import { getUserWorkspaces } from '@/workspaces/services/getWorkspaces';
import CloseIcon from '@mui/icons-material/Close';
import { removeUnsupportedChars } from '../utils/removeUnsupportedChars';
import { sortById } from '@/common/utils/sortById';

const StyledBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

interface CreateWorkspaceModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

export const CreateWorkspaceModal: FC<CreateWorkspaceModalProps> = ({
  isModalOpen,
  setIsModalOpen
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { setCurrentWorkspace, setWorkspaces } = useUserStore();
  const isMountedRef = useRefMounted();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onSubmit = async () => {
    try {
      const filteredName = removeUnsupportedChars(formik.values.workspaceName);
      const newWorkspace = await createWorkspace(filteredName);
      const updatedWorkspaces = await getUserWorkspaces();
      const sortByCurrentId = sortById(newWorkspace.id);
      const sortedWorkspaces = updatedWorkspaces.sort(sortByCurrentId);

      setWorkspaces(sortedWorkspaces);
      setCurrentWorkspace(newWorkspace.id);
      window.localStorage.setItem('currentWorkspace', newWorkspace.id);

      formik.resetForm();
      setIsModalOpen(false);
    } catch (err) {
      console.error({ err });

      if (isMountedRef()) {
        formik.setStatus({ success: false });
        formik.setFieldError('workspaceName', err[0]);
        formik.setSubmitting(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: { workspaceName: '' },
    validationSchema: Yup.object({
      workspaceName: Yup.string()
        .required('Name is required')
        .min(3, 'Name must have at least 3 characters')
        .max(20, 'Name must be less than 20 characters')
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit
  });

  const handleOnCancelCreate = async (event): Promise<void> => {
    event.preventDefault();
    await formik.setFieldValue('workspaceName', '');
    setIsModalOpen(false);
  };

  return (
    <Modal
      disableScrollLock
      onClose={handleOnCancelCreate}
      open={isModalOpen}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      aria-labelledby="centered-modal"
      aria-describedby="centered-modal-description"
    >
      <StyledBox
        sx={{
          pt: 2,
          backgroundColor: theme.colors.alpha.white[100],
          borderRadius: 1,
          minWidth: isMobile ? 270 : 410
        }}
        position="relative"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={1}
      >
        <IconButton
          aria-label="Close"
          onClick={handleOnCancelCreate}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          sx={{ pt: 2 }}
          gap={2}
        >
          <ListItemText
            primary={t('Create workspace')}
            primaryTypographyProps={{
              textAlign: 'center',
              mb: 0.5,
              fontWeight: theme.typography.fontWeightBold
            }}
          />
        </Box>

        <form
          style={{
            width: '80%'
          }}
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <TextField
            error={Boolean(
              formik.touched.workspaceName && formik.errors.workspaceName
            )}
            fullWidth
            margin="normal"
            helperText={
              formik.touched.workspaceName && formik.errors.workspaceName
            }
            name="workspaceName"
            autoFocus
            onFocus={() => {
              formik.setFieldError('workspaceName', '');
            }}
            onChange={formik.handleChange}
            type="name"
            value={formik.values.workspaceName}
            variant="outlined"
            size="medium"
            placeholder={t('Workspace name')}
            InputLabelProps={{ shrink: true }}
          />

          <Box display="flex" justifyContent="center" gap={1} mt={2}>
            <Button
              color="primary"
              startIcon={
                formik.isSubmitting ? <CircularProgress size="1rem" /> : null
              }
              disabled={!formik.values.workspaceName || formik.isSubmitting}
              type="submit"
              style={{ flex: '50%', maxWidth: '50%' }}
              variant="contained"
            >
              {t('Create')}
            </Button>
          </Box>
        </form>
      </StyledBox>
    </Modal>
  );
};
